import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Send, Image as ImageIcon, Package, Loader2, Sliders, Info, User, MessageCircle, SendHorizonal, Paperclip } from 'lucide-react';
import { ticketAPI } from '../../api';
import toast from 'react-hot-toast';
import { useGenericWebSocket } from '../../hooks/useGenericWebSocket';

export default function TicketDetails() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [ticket, setTicket] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  // Chat state
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const chatEndRef = useRef<HTMLDivElement>(null);

  // Seller info
  const [sellerInfo, setSellerInfo] = useState<any>(null);

  useEffect(() => {
    const sellerStr = localStorage.getItem('seller');
    if (sellerStr) {
      try {
        setSellerInfo(JSON.parse(sellerStr));
      } catch (e) { }
    }
  }, []);

  const handleTicketUpdate = useCallback((operation: string, updatedDocument: any) => {
    setTicket((prev: any) => {
      if (prev && updatedDocument && (updatedDocument._id === prev._id || updatedDocument.ticketId === prev.ticketId)) {
        return updatedDocument;
      }
      return prev;
    });
  }, []);

  useGenericWebSocket<any>(
    'users_support_tickets',
    handleTicketUpdate,
    {
      url: '/tickets/details',
      payload: { ticketId: id }
    }
  );

  const fetchTicketDetails = async () => {
    try {
      const res = await ticketAPI.getDetails({ ticketId: id });
      if (res.data.response === 'S') {
        setTicket(res.data.data);
      } else {
        toast.error('Ticket not found');
        navigate('/tickets');
      }
    } catch (err) {
      console.error(err);
      toast.error('Failed to load ticket details');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) fetchTicketDetails();
  }, [id]);

  useEffect(() => {
    // Scroll to bottom of chat when ticket updates
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [ticket?.chatHistory]);

  const handleSendMessage = async () => {
    if (!message.trim() || sending) return;
    setSending(true);
    try {
      const res = await ticketAPI.addMessage({ ticketId: id, message });
      if (res.data.response === 'S') {
        setMessage('');
        fetchTicketDetails(); // Refresh to get new messages
      } else {
        toast.error(res.data.message || 'Failed to send message');
      }
    } catch (err) {
      console.error(err);
      toast.error('Error sending message');
    } finally {
      setSending(false);
    }
  };

  const handleReopenTicket = async () => {
    try {
      const res = await ticketAPI.updateStatus({ ticketId: id, status: 'Open' });
      if (res.data.response === 'S') {
        toast.success('Ticket reopened successfully');
        fetchTicketDetails();
      } else {
        toast.error(res.data.message || 'Failed to reopen ticket');
      }
    } catch (err) {
      console.error(err);
      toast.error('Error reopening ticket');
    }
  };


  if (loading) return (
    <div className="fade-in" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', gap: '16px' }}>
      <Loader2 size={40} color="#14b8a6" style={{ animation: 'spin 1s linear infinite' }} />
      <div style={{ color: '#64748b', fontSize: '15px', fontWeight: '500', letterSpacing: '0.02em' }}>Loading ticket details...</div>
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
  if (!ticket) return null;

  return (
    <div className="fade-in ticket-details-container" style={{ width: '100%', background: '#f8fafc' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px' }}>
        <button onClick={() => navigate('/tickets')} style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: '8px', cursor: 'pointer', padding: '8px', display: 'flex', transition: 'all 0.2s' }}>
          <ArrowLeft size={18} color="#334155" />
        </button>
        <div>
          <h1 style={{ fontSize: '20px', fontWeight: '800', color: '#0f172a', margin: 0 }}>Ticket Details</h1>
        </div>
      </div>

      {/* Main Grid Layout */}
      <div className="main-grid" style={{ gap: '24px', alignItems: 'start' }}>

        {/* LEFT COLUMN: Info */}
        <div className="left-column hide-scroll" style={{
          display: 'flex', flexDirection: 'column', gap: '24px',
          maxHeight: 'calc(100vh - 120px)',
          overflowY: 'auto',
          paddingBottom: '24px'
        }}>

          {/* Ticket Information Card */}
          <div style={{ flexShrink: 0, background: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 1px 2px rgba(0,0,0,0.02)' }}>
            <div style={{ background: '#f8fafc', padding: '16px 24px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: '#e0f2fe', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#0284c7' }}>
                  <Info size={16} />
                </div>
                <h2 style={{ fontSize: '16px', fontWeight: '600', color: '#0f172a', margin: 0 }}>Ticket Information</h2>
              </div>
              <span style={{
                fontSize: '12px', fontWeight: '500', padding: '4px 12px', borderRadius: '16px',
                background: '#e0f2fe', color: '#0284c7'
              }}>
                {ticket.ticketId}
              </span>
            </div>

            <div style={{ padding: '24px' }}>
              <div style={{ marginBottom: '24px' }}>
                <div style={{ fontSize: '11px', color: '#64748b', fontWeight: '600', letterSpacing: '0.05em', marginBottom: '8px', textTransform: 'uppercase' }}>Subject</div>
                <div style={{ fontSize: '15px', color: '#1e293b', fontWeight: '400', lineHeight: '1.5' }}>
                  {ticket.subject || 'Support Ticket'}
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: ticket.subCategory ? 'repeat(auto-fit, minmax(140px, 1fr))' : '1fr 1fr', gap: '24px' }}>
                <div>
                  <div style={{ fontSize: '11px', color: '#64748b', fontWeight: '600', letterSpacing: '0.05em', marginBottom: '8px', textTransform: 'uppercase' }}>Category</div>
                  <div>
                    <span style={{ 
                      display: 'inline-block', padding: '4px 12px', borderRadius: '16px', 
                      background: '#f3e8ff', color: '#9333ea', fontSize: '12px', fontWeight: '500' 
                    }}>
                      {ticket.category}
                    </span>
                  </div>
                </div>
                {ticket.subCategory && (
                  <div>
                    <div style={{ fontSize: '11px', color: '#64748b', fontWeight: '600', letterSpacing: '0.05em', marginBottom: '8px', textTransform: 'uppercase' }}>Sub Category</div>
                    <div>
                      <span style={{ 
                        display: 'inline-block', padding: '4px 12px', borderRadius: '16px', 
                        background: '#f1f5f9', color: '#334155', fontSize: '12px', fontWeight: '500' 
                      }}>
                        {ticket.subCategory}
                      </span>
                    </div>
                  </div>
                )}
                <div>
                  <div style={{ fontSize: '11px', color: '#64748b', fontWeight: '600', letterSpacing: '0.05em', marginBottom: '8px', textTransform: 'uppercase' }}>Priority</div>
                  <div>
                    <span style={{ 
                      display: 'inline-block', padding: '4px 12px', borderRadius: '16px', 
                      background: ticket.priority === 'High' ? '#fee2e2' : ticket.priority === 'Low' ? '#dcfce7' : '#fef3c7', 
                      color: ticket.priority === 'High' ? '#dc2626' : ticket.priority === 'Low' ? '#16a34a' : '#d97706', 
                      fontSize: '12px', fontWeight: '500' 
                    }}>
                      {ticket.priority || 'Medium'}
                    </span>
                  </div>
                </div>
              </div>

              {(!ticket.productDetails || Object.keys(ticket.productDetails).length === 0) && (
                <div style={{ marginTop: '24px' }}>
                  <div style={{ fontSize: '11px', color: '#64748b', fontWeight: '600', letterSpacing: '0.05em', marginBottom: '8px', textTransform: 'uppercase' }}>Description</div>
                  <div style={{
                    fontSize: '14px', color: '#334155', lineHeight: '1.6', whiteSpace: 'pre-wrap'
                  }}>
                    {ticket.description || 'No description provided.'}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Creator Information Card */}
          <div style={{ flexShrink: 0, background: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 1px 2px rgba(0,0,0,0.02)' }}>
            <div style={{ background: '#faf5ff', padding: '16px 24px', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: '#f3e8ff', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9333ea' }}>
                  <User size={16} />
                </div>
                <h2 style={{ fontSize: '16px', fontWeight: '600', color: '#0f172a', margin: 0 }}>Creator Information</h2>
              </div>
            </div>

            <div style={{ padding: '24px', display: 'flex', alignItems: 'center', gap: '16px' }}>
              <div style={{ width: '48px', height: '48px', borderRadius: '50%', background: '#a855f7', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px', fontWeight: '500', color: 'white' }}>
                {(sellerInfo?.contactPerson || sellerInfo?.businessName || sellerInfo?.name || sellerInfo?.ownerName || ticket.userName || 'S').charAt(0).toUpperCase()}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <div style={{ fontSize: '15px', color: '#0f172a', fontWeight: '600' }}>{sellerInfo?.contactPerson || sellerInfo?.businessName || sellerInfo?.name || sellerInfo?.ownerName || ticket.userName || 'Seller'}</div>
                <div style={{ fontSize: '13px', color: '#64748b', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span>{sellerInfo?.phone || sellerInfo?.mobile || 'N/A'}</span>
                  <span style={{ padding: '2px 8px', borderRadius: '12px', background: '#f1f5f9', color: '#475569', fontSize: '12px', fontWeight: '500', textTransform: 'capitalize' }}>
                    {ticket.user === 'seller' ? 'Seller' : (ticket.user || 'User')}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Product Details (If applicable) */}
          {ticket.productDetails && Object.keys(ticket.productDetails).length > 0 && (
            <div style={{ flexShrink: 0, background: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 1px 2px rgba(0,0,0,0.02)' }}>
              <div style={{ background: '#fffbeb', padding: '16px 24px', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: '#fef3c7', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#d97706' }}>
                    <Package size={16} />
                  </div>
                  <h2 style={{ fontSize: '16px', fontWeight: '600', color: '#0f172a', margin: 0 }}>Product Details</h2>
                </div>
              </div>

              <div style={{ padding: '24px', display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '24px' }}>
                {Object.entries(ticket.productDetails).map(([key, value]) => {
                  if (!value || (Array.isArray(value) && value.length === 0)) return null;
                  if (key === 'keySpecifications') return null;

                  const label = key.replace(/([A-Z])/g, ' $1').trim().toUpperCase();
                  let displayValue: React.ReactNode = String(value);

                  if (label === 'TAGS') {
                    const tagsArray = Array.isArray(value) ? value : String(value).split(',').map(t => t.trim()).filter(Boolean);
                    displayValue = (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                        {tagsArray.map((tag: string, i: number) => (
                          <span key={i} style={{ padding: '4px 12px', borderRadius: '16px', border: '1px solid #bfdbfe', background: '#eff6ff', color: '#2563eb', fontSize: '12px', fontWeight: '500' }}>
                            {tag}
                          </span>
                        ))}
                      </div>
                    );
                  } else if (label === 'PRODUCT STATUS') {
                    displayValue = (
                      <span style={{ padding: '4px 12px', borderRadius: '16px', background: '#dcfce7', color: '#16a34a', fontSize: '12px', fontWeight: '500', textTransform: 'capitalize' }}>
                        {String(value)}
                      </span>
                    );
                  } else if (label === 'REPLACEMENT DAYS') {
                    displayValue = `${value}`;
                  } else {
                    displayValue = Array.isArray(value) ? value.join(', ') : String(value);
                  }

                  const getColSpan = (lbl: string) => {
                    if (lbl === 'TAGS' || lbl === 'SUB TITLE' || lbl === 'DESCRIPTION') return '1 / -1';
                    if (['HEIGHT', 'WIDTH', 'WEIGHT', 'OTHER'].includes(lbl)) return 'auto';
                    return 'span 2';
                  };

                  return (
                    <div key={key} style={{ gridColumn: getColSpan(label) }}>
                      <div style={{ fontSize: '11px', color: '#64748b', fontWeight: '600', letterSpacing: '0.05em', marginBottom: '8px' }}>
                        {label}
                      </div>
                      <div style={{ fontSize: '14px', color: '#1e293b', fontWeight: '600', wordBreak: 'break-word', lineHeight: '1.5' }}>
                        {displayValue}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Attachments */}
          {ticket.images && ticket.images.length > 0 && (
            <div style={{ flexShrink: 0, background: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 1px 2px rgba(0,0,0,0.02)' }}>
              <div style={{ background: '#f0fdf4', padding: '16px 24px', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: '#dcfce7', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#16a34a' }}>
                    <Paperclip size={16} />
                  </div>
                  <h2 style={{ fontSize: '16px', fontWeight: '600', color: '#0f172a', margin: 0 }}>Attachments</h2>
                </div>
              </div>

              <div style={{ padding: '24px', display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                {ticket.images.map((img: string, idx: number) => {
                  return (
                    <a key={idx} href={img} target="_blank" rel="noreferrer" style={{ 
                      width: '120px', height: '120px', borderRadius: '8px', background: '#f1f5f9', border: '1px solid #e2e8f0',
                      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                      transition: 'all 0.2s', cursor: 'pointer', textDecoration: 'none', overflow: 'hidden'
                    }} className="hover-scale">
                      <img 
                        src={img} 
                        alt="Attachment" 
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                          const fallback = e.currentTarget.nextElementSibling as HTMLElement;
                          if (fallback) fallback.style.display = 'flex';
                        }}
                      />
                      <div style={{ display: 'none', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
                        <Paperclip size={24} color="#94a3b8" />
                        <span style={{ fontSize: '13px', color: '#64748b' }}>File {idx + 1}</span>
                      </div>
                    </a>
                  )
                })}
              </div>
            </div>
          )}

        </div>

        {/* RIGHT COLUMN: Messages Chat */}
        <div className="right-column" style={{ display: 'flex', flexDirection: 'column', minHeight: '400px', position: 'sticky', top: '24px' }}>
          <div style={{
            background: 'white', borderRadius: '24px', border: '1px solid rgba(226, 232, 240, 0.8)',
            boxShadow: '0 10px 40px -10px rgba(0,0,0,0.08)', display: 'flex', flexDirection: 'column', height: '100%',
            overflow: 'hidden', transition: 'box-shadow 0.3s ease'
          }}>
            {/* Chat Header */}
            <div style={{ padding: '24px', borderBottom: '1px solid rgba(241, 245, 249, 0.8)', background: 'rgba(255, 255, 255, 0.9)', backdropFilter: 'blur(10px)', zIndex: 10, display: 'flex', alignItems: 'center', gap: '12px' }}>
              <MessageCircle size={20} color="#3b82f6" />
              <div>
                <h2 style={{ fontSize: '16px', fontWeight: '800', color: '#0f172a', margin: 0 }}>Messages</h2>
                <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px', fontWeight: '500' }}>Chat with Support</div>
              </div>
              <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '12px' }}>

                <span style={{
                  fontSize: '12px', fontWeight: '700', padding: '6px 14px', borderRadius: '20px',
                  background: ticket.ticketstatus === 'Open' ? '#eff6ff' : ticket.ticketstatus === 'Inprogress' ? '#fffbeb' : ticket.ticketstatus === 'Resolved' || ticket.ticketstatus === 'Approved' ? '#ecfdf5' : ticket.ticketstatus === 'Closed' ? '#f1f5f9' : ticket.ticketstatus === 'Rejected' ? '#fef2f2' : '#f1f5f9',
                  color: ticket.ticketstatus === 'Open' ? '#3b82f6' : ticket.ticketstatus === 'Inprogress' ? '#d97706' : ticket.ticketstatus === 'Resolved' || ticket.ticketstatus === 'Approved' ? '#059669' : ticket.ticketstatus === 'Closed' ? '#475569' : ticket.ticketstatus === 'Rejected' ? '#dc2626' : '#64748b'
                }}>
                  {ticket.ticketstatus || 'Open'}
                </span>
              </div>
            </div>

            {/* Chat Messages */}
            <div className="custom-scroll" style={{ flex: 1, overflowY: 'auto', padding: '24px', display: 'flex', flexDirection: 'column', gap: '20px', background: '#f8fafc', scrollBehavior: 'smooth' }}>
              {(!ticket.chatHistory || ticket.chatHistory.length === 0) ? (
                <div style={{ margin: 'auto', textAlign: 'center', color: '#94a3b8', fontSize: '15px', fontWeight: '600' }}>
                  No messages yet. Start the conversation!
                </div>
              ) : (
                ticket.chatHistory.map((msg: any, index: number) => {
                  const isSeller = msg.sender === 'Seller' || msg.sender === 'seller';
                  const msgTime = msg.timestamp ? new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '';
                  return (
                    <div key={index} style={{ display: 'flex', flexDirection: 'column', alignItems: isSeller ? 'flex-end' : 'flex-start', maxWidth: '100%' }}>
                      <div className="msg-pop" style={{
                        background: isSeller ? 'linear-gradient(135deg, #10b981, #059669)' : '#ffffff',
                        color: isSeller ? 'white' : '#1e293b',
                        padding: '12px 18px',
                        borderRadius: isSeller ? '20px 20px 4px 20px' : '20px 20px 20px 4px',
                        border: isSeller ? 'none' : '1px solid rgba(226, 232, 240, 0.8)',
                        fontSize: '15px',
                        fontWeight: '400',
                        maxWidth: '85%',
                        wordBreak: 'break-word',
                        boxShadow: isSeller ? '0 4px 12px rgba(16, 185, 129, 0.2)' : '0 4px 12px rgba(0, 0, 0, 0.04)'
                      }}>
                        {msg.message}
                      </div>
                      {msgTime && (
                        <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '6px', fontWeight: '500', padding: '0 4px' }}>
                          {msgTime}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
              <div ref={chatEndRef} />
            </div>

            {/* Chat Input */}
            <div style={{ padding: '20px 24px', background: '#ffffff', display: 'flex', flexDirection: 'column', gap: '14px', boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.02)', zIndex: 10 }}>
              {(ticket.ticketstatus === 'Closed' || ticket.ticketstatus === 'Resolved') ? (
                <div className="reopen-banner">
                  <span style={{ textAlign: 'center', lineHeight: '1.5' }}>This chat is {ticket.ticketstatus}. Click the button to reopen if you need further assistance.</span>
                  <button
                    onClick={handleReopenTicket}
                    style={{
                      width: '100%',
                      padding: '12px 24px',
                      borderRadius: '8px',
                      border: 'none',
                      background: '#e0e7ff', // light blue background
                      color: '#1e1b4b', // dark text color
                      fontSize: '14px',
                      fontWeight: '700',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}
                    className="hover-scale"
                  >
                    Reopen Ticket
                  </button>
                </div>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', background: '#f8fafc', borderRadius: '9999px', border: '1px solid #e2e8f0', padding: '6px 6px 6px 20px', transition: 'border-color 0.3s' }} className="chat-input-wrapper">
                  <input
                    type="text"
                    placeholder="Type your message..."
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleSendMessage(); }}
                    style={{ flex: 1, background: 'transparent', border: 'none', outline: 'none', fontSize: '15px', color: '#1e293b', padding: '10px 0' }}
                  />
                  <button
                    onClick={handleSendMessage}
                    disabled={sending || !message.trim()}
                    style={{
                      background: message.trim() && !sending ? 'linear-gradient(135deg, #10b981, #059669)' : '#cbd5e1',
                      color: 'white',
                      border: 'none',
                      width: '48px', height: '48px',
                      borderRadius: '50%',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      cursor: message.trim() && !sending ? 'pointer' : 'not-allowed',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      marginLeft: '12px',
                      boxShadow: message.trim() && !sending ? '0 4px 12px rgba(16, 185, 129, 0.3)' : 'none'
                    }}
                    className={message.trim() && !sending ? "hover-scale-btn" : ""}
                  >
                    {sending ? <Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} /> : <SendHorizonal size={20} />}
                  </button>
                </div>
              )}
            </div>

          </div>
        </div>

      </div>

      <style>{`
        .hover-scale:hover {
          transform: scale(1.05);
        }
        .hide-scroll::-webkit-scrollbar {
          display: none;
        }
        .hide-scroll {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        @keyframes popIn {
          0% { opacity: 0; transform: scale(0.9) translateY(10px); }
          100% { opacity: 1; transform: scale(1) translateY(0); }
        }
        .msg-pop {
          animation: popIn 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards;
        }
        .custom-scroll::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scroll::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-scroll::-webkit-scrollbar-thumb {
          background-color: #cbd5e1;
          border-radius: 20px;
        }
        .hover-scale-btn:hover {
          transform: translateY(-2px) scale(1.05);
          box-shadow: 0 6px 16px rgba(16, 185, 129, 0.4) !important;
        }
        .hover-scale-btn:active {
          transform: translateY(0) scale(0.95);
        }
        .chat-input-wrapper:focus-within {
          background: #ffffff !important;
          border-color: #10b981 !important;
          box-shadow: 0 0 0 4px rgba(16, 185, 129, 0.15) !important;
        }

        /* Responsive Layout styles */
        .ticket-details-container {
          padding: 24px;
        }
        .reopen-banner {
          width: 100%;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 16px;
          color: #64748b;
          font-size: 14px;
          font-weight: 500;
          padding: 10px 0;
        }

        @media (max-width: 768px) {
          .ticket-details-container {
            padding: 2px !important;
          }
          .reopen-banner {
            gap: 12px !important;
          }
        }

        .main-grid {
          display: grid;
          grid-template-columns: minmax(0, 1.6fr) minmax(0, 1fr);
        }
        .left-column {
          height: calc(100vh - 236px);
          overflow-y: auto;
        }
        .right-column {
          height: calc(100vh - 236px);
        }

        .info-grid-2 {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px;
        }
        .info-grid-3 {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 16px;
        }

        @media (max-width: 1024px) {
          .main-grid {
            display: flex !important;
            flex-direction: column;
          }
          .left-column {
            height: auto !important;
            overflow-y: visible !important;
            background: transparent !important;
            border: none !important;
            padding: 0 !important;
          }
          .info-grid-2, .info-grid-3 {
            grid-template-columns: 1fr;
          }
          .right-column {
            height: 600px !important;
            position: relative !important;
            top: 0 !important;
          }
        }
      `}</style>
    </div>
  );
}

